import { useState } from 'react';
import { FormFieldsData, FormProvider } from '../FormContext';
import useForm from '../formHelper';
import type { FormTemplateProps } from '../FormTemplate';
import { FormShowContextProvider } from './FormShowContext';
import { useBaseValidationMapping } from 'validation-schema-library';
import Loading from '../../ui/mui-builder/Loading/Loading';
import SmallLoader from '../../ui/mui-builder/Loading/SmallLoader';
import Prompt from '../../utils/Prompt/Prompt';
import { ControlledShow } from 'framework/show';
import { useGetData } from 'framework/utils';

type FormShowTemplateProps = FormTemplateProps & {
  mapDataForForm?: (showData: FormFieldsData) => FormFieldsData | Promise<FormFieldsData>;
  hasLoadingBackground?: boolean;
  hassLoadingPositionFixed?: boolean;
  isInitialsLoadingPosition?: boolean;
  initEditMode?: boolean;
};

const FormShowTemplate = ({
  children,
  initialData = {},
  getValidationSchemaFunc,
  handleErrorsOnSubmit,
  isNeedToHandleErrorsOnSubmit = true,
  fieldsExceptions,
  isCreateRequest = true,
  submitFormFunc,
  afterSubmit,
  getDataFunc = null,
  handleDirty = true,
  errorsMap = {},
  messageContext,
  isSmallLoader,
  initEditMode = false,
  mapDataForForm,
  hasLoadingBackground = false,
  hassLoadingPositionFixed = false,
  isInitialsLoadingPosition = false,
}: FormShowTemplateProps) => {
  const [isEditMode, setEditMode] = useState<boolean>(initEditMode);
  const [data, setData] = useState<FormFieldsData>(initialData);

  const toggleEditMode = () => {
    setEditMode(prev => !prev);
  };

  const baseErrorMap = useBaseValidationMapping();
  const { isDirty, setFormData, resetFormData, ...contextValue } = useForm({
    initialData,
    afterSubmit: {
      ...afterSubmit,
      resetData: newData => newData,
      execute: (response: any) => {
        const { execute } = afterSubmit || {};
        if (execute) {
          execute(response);
        }
        toggleEditMode();
        setData(prev => ({
          ...prev,
          ...response,
        }));
      },
    },
    submitFormFunc,
    handleErrorsOnSubmit,
    isNeedToHandleErrorsOnSubmit,
    getValidationSchemaFunc,
    fieldsExceptions,
    handleDirty,
    isCreateRequest,
    errorsMap: {
      ...baseErrorMap,
      ...errorsMap,
    },
    messageContext,
  });

  const setDataMethod = async (newState: FormFieldsData) => {
    if (newState) {
      setData(newState);
      const mappedData = mapDataForForm ? await mapDataForForm(newState) : newState;
      setFormData(mappedData);
    }
  };

  const toggleEditModeWithResetForm = async () => {
    const mappedData = mapDataForForm ? await mapDataForForm(data) : data;
    resetFormData(mappedData);
    setEditMode(prev => !prev);
  };

  const { loading } = useGetData(getDataFunc, setDataMethod, getDataFunc !== null);

  if (loading) {
    return isSmallLoader ? (
      <SmallLoader />
    ) : (
      <Loading
        hasBackground={hasLoadingBackground}
        hasPositionFixed={hassLoadingPositionFixed}
        isInitialPosition={isInitialsLoadingPosition}
      />
    );
  }

  return (
    <FormShowContextProvider
      value={{
        isEditMode,
        toggleEditMode: toggleEditModeWithResetForm,
      }}
    >
      <ControlledShow data={data} setData={setDataMethod}>
        <FormProvider
          value={{
            isDirty,
            setFormData,
            resetFormData,
            ...contextValue,
          }}
        >
          <Prompt when={isDirty} message="Are you sure you want to go away? Unsaved data will be lost." />
          <>{children}</>
        </FormProvider>
      </ControlledShow>
    </FormShowContextProvider>
  );
};

export default FormShowTemplate;
